import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { compressImage } from "@/lib/imageCompress";
import { Sparkles, MapPin, Clock, CheckCircle2, Camera, AlertCircle, X, LogOut } from "lucide-react";

const STATUSES = [
  { v: "limpio", l: "Limpio", color: "#10B981" },
  { v: "requiere_atencion", l: "Requiere atención", color: "#F59E0B" },
  { v: "insumos_faltantes", l: "Faltan insumos", color: "#F59E0B" },
  { v: "problema", l: "Hay un problema", color: "#EF4444" },
];

const SUPPLIES = ["Papel", "Jabón", "Toallas", "Aromatizante", "Bolsas de basura"];

export default function CheckInPage() {
  const { areaId } = useParams();
  const { user, logout, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("limpio");
  const [notes, setNotes] = useState("");
  const [supplies, setSupplies] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [gps, setGps] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (user === false) {
      nav(`/login`, { state: { from: `/check/${areaId}` }, replace: true });
      return;
    }
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get(`/areas/${areaId}`);
        setInfo(data);
      } catch (e) {
        setErr(formatApiError(e));
      } finally { setLoading(false); }
    })();
    // request GPS (silently — non blocking)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGps({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => setGps(null),
        { timeout: 5000 }
      );
    }
  }, [user, authLoading, areaId, nav]);

  const onPhotoSelect = async (e) => {
    const raw = e.target.files?.[0]; if (!raw) return;
    setPhotoFile(raw); setPhotoPreview(URL.createObjectURL(raw));
    setUploading(true); setUploadProgress(0); setErr("");
    try {
      // Compress large Android photos (12-20MB → ~400-800KB). iPhone HEIC is kept.
      const f = await compressImage(raw, { maxDim: 1600, quality: 0.85 });
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000, // 2 min — covers slow mobile data
        onUploadProgress: (ev) => {
          if (ev.total) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      setPhotoUrl(data.url);
      setUploadProgress(100);
    } catch (e) {
      setErr(formatApiError(e));
      setPhotoFile(null); setPhotoPreview(null);
      if (inputRef.current) inputRef.current.value = "";
    }
    finally { setUploading(false); }
  };

  const removePhoto = () => { setPhotoFile(null); setPhotoPreview(null); setPhotoUrl(null); setUploadProgress(0); if (inputRef.current) inputRef.current.value = ""; };

  const submit = async () => {
    if (info?.area?.photo_required && !photoUrl) {
      setErr("Esta área requiere foto de evidencia para completar el check.");
      return;
    }
    setSubmitting(true); setErr("");
    try {
      await api.post("/checkins", {
        area_id: areaId, status, notes: notes.trim() || null, photo_url: photoUrl,
        latitude: gps?.latitude, longitude: gps?.longitude,
        device_info: navigator.userAgent.slice(0, 200),
        supplies: Object.keys(supplies).length ? supplies : null,
      }, { timeout: 60000 });
      setSubmitted(true);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (authLoading || (loading && user)) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-lx-primary border-t-transparent rounded-full animate-spin"/></div>;
  }
  if (err && !info) {
    return <div className="min-h-screen p-6 flex items-center justify-center">
      <div className="lx-card max-w-md w-full text-center">
        <AlertCircle size={36} className="mx-auto text-lx-error mb-3"/>
        <h2 className="font-heading font-bold text-xl">No se pudo cargar el área</h2>
        <p className="text-sm text-lx-muted mt-2">{err}</p>
      </div>
    </div>;
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header user={user} onLogout={async () => { await logout(); nav("/login"); }}/>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-testid="checkin-success">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mb-5" style={{ background: "#10B98122" }}>
            <CheckCircle2 size={48} color="#10B981" strokeWidth={1.8}/>
          </div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">¡Limpieza registrada!</h1>
          <p className="text-lx-muted mt-2 max-w-sm">El registro se guardó correctamente para <span className="font-semibold text-lx-text">{info?.area?.name}</span>.</p>
          <div className="mt-8 grid grid-cols-1 gap-3 w-full max-w-xs">
            <button className="lx-btn lx-btn-primary lx-btn-mobile" onClick={() => window.location.reload()} data-testid="new-checkin-button">
              Registrar otra limpieza
            </button>
            <button className="lx-btn lx-btn-secondary lx-btn-mobile" onClick={() => nav("/")}>Ir al panel</button>
          </div>
        </div>
      </div>
    );
  }

  const area = info?.area; const loc = info?.location; const cli = info?.client; const last = info?.last_checkin;
  const goal = area?.expected_per_day || 3;

  if (info?.goal_reached) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header user={user} onLogout={async () => { await logout(); nav("/login"); }}/>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-testid="checkin-goal-reached">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mb-5" style={{ background: "#10B98122" }}>
            <CheckCircle2 size={48} color="#10B981" strokeWidth={1.8}/>
          </div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Meta del día cumplida</h1>
          <p className="text-lx-muted mt-2 max-w-sm">
            <span className="font-semibold text-lx-text">{area?.name}</span> ya tiene {info.today_count} de {goal} limpiezas registradas hoy. No se pueden registrar más checks en esta área hasta mañana.
          </p>
          <button className="lx-btn lx-btn-secondary lx-btn-mobile mt-8 w-full max-w-xs" onClick={() => nav("/")} data-testid="goal-reached-back">Ir al panel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-32">
      <Header user={user} onLogout={async () => { await logout(); nav("/login"); }}/>

      <div className="max-w-lg mx-auto px-5 pt-5">
        {/* Area header */}
        <div className="rounded-2xl p-5 text-white" style={{ background: "linear-gradient(135deg, #00AEEF, #008CBE)" }} data-testid="area-header">
          <div className="text-xs uppercase tracking-widest opacity-80 font-bold">Check-in de limpieza</div>
          <h1 className="font-heading font-bold text-3xl tracking-tight mt-1">{area?.name}</h1>
          <div className="mt-3 space-y-1 text-sm opacity-95">
            {loc && <div className="flex items-center gap-1.5"><MapPin size={14}/> {loc.name}</div>}
            {cli && <div className="flex items-center gap-1.5"><Sparkles size={14}/> {cli.name}</div>}
            <div className="flex items-center gap-1.5 font-semibold" data-testid="today-progress">
              <CheckCircle2 size={14}/> Hoy: {info?.today_count ?? 0} de {goal} limpiezas
            </div>
            {last && (
              <div className="flex items-center gap-1.5 opacity-80">
                <Clock size={14}/> Último check: {new Date(last.created_at).toLocaleString("es-MX", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
              </div>
            )}
          </div>
          {area?.instructions && (
            <div className="mt-4 p-3 rounded-lg bg-white/15 text-sm" data-testid="area-instructions">
              <strong>Instrucciones:</strong> {area.instructions}
            </div>
          )}
        </div>

        {/* Status picker */}
        <div className="mt-5">
          <div className="lx-overline mb-3">Estado del área</div>
          <div className="grid grid-cols-2 gap-2">
            {STATUSES.map(s => (
              <button
                key={s.v}
                type="button"
                onClick={() => setStatus(s.v)}
                className={`p-3 rounded-xl border-2 text-left transition ${status === s.v ? "border-lx-primary bg-lx-primary/5" : "border-lx-border bg-white"}`}
                data-testid={`status-${s.v}`}
              >
                <div className="w-2 h-2 rounded-full mb-2" style={{ background: s.color }}/>
                <div className="font-semibold text-sm">{s.l}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Supplies (only when not "limpio") */}
        {status === "insumos_faltantes" && (
          <div className="mt-5">
            <div className="lx-overline mb-3">Insumos faltantes</div>
            <div className="flex flex-wrap gap-2">
              {SUPPLIES.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSupplies({ ...supplies, [s]: !supplies[s] })}
                  className={`px-3 py-2 rounded-full text-sm border ${supplies[s] ? "bg-lx-primary text-white border-lx-primary" : "bg-white text-lx-text border-lx-border"}`}
                  data-testid={`supply-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="mt-5">
          <label className="lx-label">Observaciones {area?.photo_required && status !== "limpio" ? "" : "(opcional)"}</label>
          <textarea
            rows={3}
            className="lx-input"
            placeholder="Detalles importantes…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="checkin-notes"
          />
        </div>

        {/* Photo */}
        <div className="mt-5">
          <label className="lx-label">
            Foto de evidencia {area?.photo_required ? <span className="text-lx-error">*</span> : <span className="text-lx-muted font-normal">(opcional)</span>}
          </label>
          {photoPreview ? (
            <div className="relative inline-block">
              <img src={photoPreview} alt="Evidencia" className="rounded-lg max-h-60 border border-lx-border" data-testid="photo-preview"/>
              <button onClick={removePhoto} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1" data-testid="remove-photo"><X size={14}/></button>
              {uploading && (
                <div className="absolute inset-0 bg-white/80 rounded-lg flex flex-col items-center justify-center gap-2" data-testid="upload-overlay">
                  <div className="w-7 h-7 border-2 border-lx-primary border-t-transparent rounded-full animate-spin"/>
                  <div className="text-xs font-semibold text-lx-primary">
                    {uploadProgress > 0 && uploadProgress < 100 ? `Subiendo ${uploadProgress}%` : "Procesando foto…"}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <label className="lx-btn lx-btn-secondary lx-btn-mobile cursor-pointer" data-testid="take-photo-button">
              <Camera size={20}/> Tomar foto
              <input ref={inputRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhotoSelect}/>
            </label>
          )}
        </div>

        {err && <div className="mt-4 text-sm rounded-lg px-3 py-2.5 bg-red-50 text-red-700 border border-red-200" data-testid="checkin-error">{err}</div>}
      </div>

      {/* Sticky submit */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-lx-border p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
        <div className="max-w-lg mx-auto">
          <button
            onClick={submit}
            disabled={submitting || uploading}
            className="lx-btn lx-btn-primary lx-btn-mobile"
            data-testid="submit-checkin-button"
          >
            {submitting ? "Registrando…" : <><CheckCircle2 size={22}/> Registrar limpieza</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Header({ user, onLogout }) {
  return (
    <div className="border-b border-lx-border bg-white px-5 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "#00AEEF" }}>
          <Sparkles size={16} color="#fff"/>
        </div>
        <div className="font-heading font-bold text-sm">Limpio<span className="text-lx-primary">Express</span></div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-lx-muted hidden sm:inline">{user?.name}</span>
        <button onClick={onLogout} className="p-2 rounded-lg hover:bg-lx-surface" data-testid="checkin-logout"><LogOut size={16}/></button>
      </div>
    </div>
  );
}
